// src/pages/IncidentTypeAllocation.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

export default function IncidentTypeAllocation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const memberId = searchParams.get("member_id") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [guard, setGuard] = useState(null);
  const [types, setTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    if (!memberId) {
      setErrorMessage("Missing member_id.");
      setLoading(false);
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate, memberId]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, permissionRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/incident-permissions/${memberId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!permissionRes.ok) throw new Error("Failed to fetch incident permissions");

      const dashboardResult = await dashboardRes.json();
      const permissionResult = await permissionRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      if (!permissionResult?.success) {
        throw new Error(permissionResult?.message || "Failed to load data");
      }

      setGuard(permissionResult.guard || null);
      setTypes(permissionResult.types || []);
      setSelectedIds((permissionResult.selectedIds || []).map(Number));
    } catch (error) {
      console.error("Incident permission fetch error:", error);
      setErrorMessage(error.message || "Failed to load incident permissions.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const filteredTypes = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return types.filter((item) => {
      if (!search) return true;

      return (
        String(item.label || "").toLowerCase().includes(search) ||
        String(item.type_key || "").toLowerCase().includes(search)
      );
    });
  }, [types, searchTerm]);

  const toggleType = (id) => {
    const typeId = Number(id);

    setSelectedIds((prev) =>
      prev.includes(typeId)
        ? prev.filter((item) => item !== typeId)
        : [...prev, typeId]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = filteredTypes.map((item) => Number(item.id));
    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const clearVisible = () => {
    const visibleIds = filteredTypes.map((item) => Number(item.id));
    setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
  };

  const selectOnlyActive = () => {
    const activeIds = types
      .filter((item) => Number(item.is_active) === 1 || item.is_active === true)
      .map((item) => Number(item.id));

    setSelectedIds(activeIds);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/incident-permissions/${memberId}/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          member_id: memberId,
          type_ids: selectedIds,
        }),
      });

      const result = await res.json();

      if (!res.ok || !result?.success) {
        throw new Error(result?.message || "Save failed");
      }

      setSuccessMessage(result.message || "Incident permissions saved successfully.");
      setTimeout(() => {
  navigate("/add_incidents");
}, 800);
    } catch (error) {
      console.error("Incident permission save error:", error);
      setErrorMessage(error.message || "Failed to save incident permissions.");
    } finally {
      setSaving(false);
    }
  };

  const backToUsers = () => navigate("/user_list");

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-100">
        <div className="mb-3 h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
        <p className="text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading incident permissions…
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="user-list"
    >
      <PageHeader onBack={backToUsers} />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <GuardCard guard={guard} memberId={memberId} />

      <FilterCard
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onSelectAll={selectAllVisible}
        onClear={clearVisible}
        onOnlyActive={selectOnlyActive}
      />

      <IncidentGrid
        types={filteredTypes}
        selectedIds={selectedIds}
        onToggle={toggleType}
      />

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={backToUsers}
          className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          style={{ fontFamily: FONT_UI }}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-green-600/20 transition hover:opacity-95 disabled:opacity-60"
          style={{
            fontFamily: FONT_UI,
            background: "#16a34a",
          }}
        >
          {saving ? "Saving..." : "Save Permissions"}
        </button>
      </div>
    </Layout>
  );
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function PageHeader({ onBack }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal tracking-[-0.3px] text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          Incident Permission Management
        </h2>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Assign incident types to selected guard
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white px-7 py-6 shadow-sm">
        <div>
          <h3
            className="mb-1 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            Incident Type Allocation
          </h3>

          <p className="text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Select the incident categories this user can access
          </p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:opacity-95"
          style={{
            fontFamily: FONT_UI,
            background: skin.gradient,
          }}
        >
          Back to Users
        </button>
      </div>
    </>
  );
}

function GuardCard({ guard, memberId }) {
  if (!guard) return null;

  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white px-7 py-6 shadow-sm">
      <h3
        className="mb-2 text-[20px] font-normal text-slate-900"
        style={{ fontFamily: FONT_HEADING }}
      >
        {guard.name || "User"}{" "}
        <span className="text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          · {memberId}
        </span>
      </h3>

      <div className="grid grid-cols-1 gap-3 text-sm text-slate-600 md:grid-cols-3">
        <div style={{ fontFamily: FONT_UI }}>
          <strong>Username:</strong> {guard.username || "—"}
        </div>
        <div style={{ fontFamily: FONT_UI }}>
          <strong>Role:</strong> {guard.role || "Guard"}
        </div>
        <div style={{ fontFamily: FONT_UI }}>
          <strong>Status:</strong> {guard.status || "—"}
        </div>
      </div>
    </div>
  );
}

function FilterCard({
  searchTerm,
  setSearchTerm,
  onSelectAll,
  onClear,
  onOnlyActive,
}) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-4 rounded-2xl border border-slate-100 bg-white px-6 py-5 shadow-sm lg:grid-cols-[2fr_auto_auto_auto]">
      <Input
        type="text"
        placeholder="Search incident type or key..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <Button onClick={onSelectAll}>Select All</Button>
      <Button onClick={onClear}>Clear</Button>
      <Button onClick={onOnlyActive}>Only Active</Button>
    </div>
  );
}

function IncidentGrid({ types, selectedIds, onToggle }) {
  const skin = useSkin();

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
      <div
        className="px-6 py-4 text-sm font-bold tracking-wide text-white"
        style={{
          fontFamily: FONT_UI,
          background: skin.gradient,
        }}
      >
        Incident Types
      </div>

      <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
        {types.length === 0 ? (
          <div
            className="col-span-full rounded-xl border border-slate-100 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500"
            style={{ fontFamily: FONT_UI }}
          >
            No incident types found.
          </div>
        ) : (
          types.map((item) => {
            const id = Number(item.id);
            const checked = selectedIds.includes(id);
            const inactive = Number(item.is_active) !== 1 && item.is_active !== true;

            return (
              <label
                key={id}
                className={`flex cursor-pointer items-center gap-4 rounded-2xl border px-4 py-4 transition ${
                  checked
                    ? "border-blue-300 bg-blue-50"
                    : "border-slate-100 bg-white hover:bg-slate-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(id)}
                  className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />

                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white"
                  style={{
                    background: item.color || skin.gradient,
                    fontFamily: FONT_UI,
                  }}
                >
                  {item.icon_value || "!"}
                </div>

                <div className="min-w-0">
                  <div
                    className="truncate text-sm font-bold text-slate-900"
                    style={{ fontFamily: FONT_UI }}
                  >
                    {item.label || "—"}
                  </div>

                  <div className="mt-1 text-xs text-slate-500" style={{ fontFamily: FONT_UI }}>
                    {item.type_key || "—"}
                    {inactive ? " · inactive" : ""}
                  </div>
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 rounded-xl border px-4 py-3 text-sm font-medium ${
        isSuccess
          ? "border-green-200 bg-green-50 text-green-700"
          : "border-red-200 bg-red-50 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Button({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </button>
  );
}